import React, { useEffect, useRef } from "react";

interface ParticleProps {
  size: number;
  speedX: number;
  speedY: number;
  x: number;
  y: number;
}

class Particle {
  size: number;
  speedX: number;
  speedY: number;
  x: number;
  y: number;

  // Add speed limits as constants
  static MIN_SPEED = -0.5;
  static MAX_SPEED = 0.5;

  constructor({ size, speedX, speedY, x, y }: ParticleProps) {
    this.size = size;
    // Apply speed limits
    this.speedX = Math.min(
      Math.max(speedX, Particle.MIN_SPEED),
      Particle.MAX_SPEED
    );
    this.speedY = Math.min(
      Math.max(speedY, Particle.MIN_SPEED),
      Particle.MAX_SPEED
    );
    this.x = x;
    this.y = y;
  }

  update(canvasWidth: number, canvasHeight: number) {
    this.x += this.speedX;
    this.y += this.speedY;

    if (this.x > canvasWidth || this.x < 0) {
      this.speedX = -this.speedX;
    }
    if (this.y > canvasHeight || this.y < 0) {
      this.speedY = -this.speedY;
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fillStyle = "aqua";
    ctx.fill();
  }
}

const ParticlesCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const paintBackground = () => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const outerRadius = Math.hypot(canvas.width, canvas.height) / 2;
      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        outerRadius
      );

      gradient.addColorStop(0, "rgba(54, 127, 245, 0.8)");
      gradient.addColorStop(0.35, "rgba(30, 76, 150, 0.85)");
      gradient.addColorStop(0.7, "rgba(10, 28, 58, 0.96)");
      gradient.addColorStop(1, "rgba(5, 4, 13, 1)");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    setCanvasSize();
    paintBackground();

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      return;
    }

    const numParticles = window.innerWidth * window.innerHeight * 0.00008;
    const particles: Particle[] = [];
    let animationFrameId = 0;

    const handleResize = () => {
      setCanvasSize();
    };

    window.addEventListener("resize", handleResize);

    for (let i = 0; i < numParticles; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      // Adjust speed limits as needed
      const speedX = (Math.random() - 0.5) * 0.2; // Example: Adjust the range
      const speedY = (Math.random() - 0.5) * 0.2; // Example: Adjust the range
      const size = Math.random() * 2.5 + 1.5;
      particles.push(new Particle({ size, speedX, speedY, x, y }));
    }

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      paintBackground();

      for (const particle of particles) {
        particle.update(canvas.width, canvas.height);
      }

      const connectionDistance = 100;
      const adjacency = new Uint8Array(
        particles.length * particles.length
      );
      const forwardNeighbors: number[][] = Array.from(
        { length: particles.length },
        () => []
      );
      const edges: Array<{ from: number; to: number; opacity: number }> = [];

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            adjacency[i * particles.length + j] = 1;
            forwardNeighbors[i].push(j);
            edges.push({
              from: i,
              to: j,
              opacity: 1 - distance / connectionDistance,
            });
          }
        }
      }

      // Tint a stable selection of closed triangular graph faces.
      for (let i = 0; i < forwardNeighbors.length; i++) {
        for (const j of forwardNeighbors[i]) {
          for (const k of forwardNeighbors[j]) {
            if (!adjacency[i * particles.length + k]) continue;

            const hash =
              (((i + 1) * 73856093) ^
                ((j + 1) * 19349663) ^
                ((k + 1) * 83492791)) >>>
              0;

            if (hash % 4 !== 0) continue;

            const opacity = 0.04 + ((hash >>> 8) % 100) * 0.001;
            ctx.fillStyle = `rgba(0, 255, 255, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.lineTo(particles[k].x, particles[k].y);
            ctx.closePath();
            ctx.fill();
          }
        }
      }

      // Keep edges and vertices crisp above the translucent faces.
      for (const edge of edges) {
        ctx.strokeStyle = `rgba(255, 255, 255, ${edge.opacity})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(particles[edge.from].x, particles[edge.from].y);
        ctx.lineTo(particles[edge.to].x, particles[edge.to].y);
        ctx.stroke();
      }

      for (const particle of particles) {
        particle.draw(ctx);
      }
    };

    animate();

    // Cleanup on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "black",
        pointerEvents: "none",
      }}
      aria-hidden="true"
    />
  );
};

export default ParticlesCanvas;
